import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, MapPin } from "lucide-react";
import { addDays, format, startOfWeek, isSameDay, parseISO } from "date-fns";
import { fr, enUS, es, pt, arSA } from "date-fns/locale";

const DF = { fr, en: enUS, es, pt, ar: arSA };
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories, useDirectory } from "@/lib/hooks";
import { PageHeader, StatusBadge } from "@/components/common";
import { ProviderBanner } from "@/pages/Tickets";

function Slot({ it }) {
  const { label } = useCategories();
  const { t } = usePrefs();
  return (
    <Link to={`/tickets/${it.id}`} data-testid={`planning-item-${it.id}`}
      className={`block rounded-sm border-l-2 bg-white p-2.5 text-xs shadow-sm transition-colors hover:bg-slate-50 ${it.kind === "conciergerie" ? "border-[#B8860B]" : it.urgency === "urgente" ? "border-rose-600" : "border-[#0F172A]"}`}>
      <div className="flex items-center gap-1 font-data text-[11px] text-slate-500"><Clock size={11} />{format(parseISO(it.when), "HH:mm")}</div>
      <div className="mt-0.5 font-semibold text-slate-900">{it.kind === "conciergerie" ? label(it.service) : it.title}</div>
      <div className="mt-0.5 flex items-center gap-1 text-slate-500"><MapPin size={10} />{it.property_name}{it.unit_ref ? ` · ${it.unit_ref}` : ""}</div>
      <div className="mt-1 text-slate-500">{it.assigned_name || "—"}</div>
      <div className="mt-1.5"><StatusBadge value={it.status} /></div>
      {it.kind === "conciergerie" && <span className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider text-[#996515]">{t("concierge")}</span>}
    </Link>
  );
}

export default function Planning() {
  const { user } = useAuth();
  const { t, lang } = usePrefs();
  const locale = DF[lang] || fr;
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const providers = useDirectory("prestataire", staff);
  const [start, setStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [prov, setProv] = useState("all");
  const end = addDays(start, 7);
  const qs = `start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}${prov !== "all" ? `&provider_id=${prov}` : ""}`;
  const { data = [] } = useQuery({ queryKey: ["planning", qs], queryFn: () => fetcher(`/planning?${qs}`), refetchInterval: 20000 });
  const days = [...Array(7)].map((_, i) => addDays(start, i));
  return (
    <div data-testid="planning-page">
      <PageHeader eyebrow={t("secOps")} title={t("planning")}>
        {staff && (
          <Select value={prov} onValueChange={setProv}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="planning-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allProviders")}</SelectItem>{providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </PageHeader>
      <ProviderBanner />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setStart(addDays(start, -7))} data-testid="planning-prev-week"><ChevronLeft size={14} />{t("prevWeek")}</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="planning-this-week">{t("thisWeek")}</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setStart(addDays(start, 7))} data-testid="planning-next-week">{t("nextWeek")}<ChevronRight size={14} /></Button>
        <span className="ml-2 font-display text-lg font-bold" data-testid="planning-range">{format(start, "d MMM", { locale })} – {format(addDays(start, 6), "d MMM yyyy", { locale })}</span>
      </div>
      <div className="grid gap-3 md:grid-cols-7">
        {days.map((d) => {
          const items = data.filter((it) => isSameDay(parseISO(it.when), d));
          const today = isSameDay(d, new Date());
          return (
            <div key={d.toISOString()} className={`min-h-[140px] rounded-sm border p-2 ${today ? "border-[#B8860B] bg-[#fdf8ec]" : "border-slate-200 bg-slate-50/60"}`} data-testid={`planning-day-${format(d, "yyyy-MM-dd")}`}>
              <div className="mb-2 flex items-baseline justify-between px-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{format(d, "EEE", { locale })}</span>
                <span className={`font-data text-sm ${today ? "font-bold text-[#996515]" : "text-slate-700"}`}>{format(d, "dd")}</span>
              </div>
              <div className="space-y-2">{items.map((it) => <Slot key={it.id} it={it} />)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
